<template>
  <div class="data-items-list">
    <!-- Aggregation info banner -->
    <div class="aggregation-info">
      <font-awesome-icon :icon="['fas', 'info-circle']" />
      <span>Showing data items from this post and all replies</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading && dataItems.length === 0" class="loading-container">
      <div class="spinner"></div>
      <p class="loading-text">Loading data items...</p>
    </div>

    <!-- Data Items -->
    <div v-else-if="dataItems.length > 0" class="items-container">
      <div
        v-for="item in dataItems"
        :key="item.id"
        class="data-item-card"
        :class="{ clickable: isClickable(item) }"
        @click="handleItemClick(item)"
      >
        <!-- Header Row: Type Badge and Category -->
        <div class="item-header">
          <span class="type-badge" :class="getTypeBadgeClass(item.item_type)">
            {{ getTypeLabel(item.item_type) }}
          </span>
          <span v-if="item.category" class="category-badge">
            {{ getCategoryLabel(item.category) }}
          </span>
        </div>

        <!-- Value Display -->
        <div class="item-value">
          {{ getItemValue(item) }}
        </div>

        <!-- Description -->
        <div v-if="item.description" class="item-description">
          {{ item.description }}
        </div>

        <!-- Footer: Attribution and Timestamp -->
        <div class="item-footer">
          <div v-if="item.attribution" class="provider-info">
            <ProviderThumbnail
              :data="{ name: item.attribution, thumbnail_url: null }"
            />
            <span class="provider-name">{{ item.attribution }}</span>
          </div>
          <span class="timestamp">
            {{ formatTimestamp(item.created_at) }}
          </span>
        </div>
      </div>

      <!-- Intersection Observer Trigger -->
      <div ref="loadMoreTrigger" class="load-more-trigger"></div>

      <!-- Loading More Indicator -->
      <div v-if="loadingMore" class="loading-more">
        <div class="spinner-small"></div>
        <span>Loading more...</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <svg
        class="empty-icon"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
      <p class="empty-text">No data items found.</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import Submission from '@/api/Submission.js'
import ProviderThumbnail from '@/components/Anonymization/ProviderThumbnail.vue'
import moment from 'moment'

const props = defineProps({
  submissionId: {
    type: Number,
    required: true
  }
})

const router = useRouter()

// State
const dataItems = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const offset = ref(0)
const limit = 20
const hasMore = ref(true)
const loadMoreTrigger = ref(null)
let observer = null

// Fetch data items
const fetchDataItems = async (isLoadMore = false) => {
  if (!hasMore.value && isLoadMore) return
  if (loading.value || loadingMore.value) return

  if (isLoadMore) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  try {
    const response = await Submission.getSubmissionDataAggregated(
      props.submissionId,
      offset.value,
      limit
    )

    if (response.status === 'success') {
      // Handle nested response structure
      const responseData = response.data || {}
      const itemsArray = responseData.items || []

      if (isLoadMore) {
        dataItems.value = [...dataItems.value, ...itemsArray]
      } else {
        dataItems.value = itemsArray
      }

      hasMore.value = responseData.has_more || false
      offset.value += itemsArray.length
    } else {
      hasMore.value = false
    }
  } catch (error) {
    console.error('Error fetching data items:', error)
    hasMore.value = false
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// Get item type label
const getTypeLabel = (itemType) => {
  const typeMap = {
    1: 'IP',
    2: 'Domain',
    3: 'URL'
  }
  return typeMap[itemType] || 'Unknown'
}

// Get type badge CSS class
const getTypeBadgeClass = (itemType) => {
  const classMap = {
    1: 'type-ip',
    2: 'type-domain',
    3: 'type-url'
  }
  return classMap[itemType] || ''
}

// Get category label
const getCategoryLabel = (category) => {
  const categoryMap = {
    vpn: 'VPN',
    proxy: 'Proxy',
    residential_proxy: 'Residential Proxy'
  }
  return categoryMap[category] || category
}

// Get item value from appropriate field
const getItemValue = (item) => {
  if (item.ip) return item.ip
  if (item.domain) return item.domain
  if (item.txt) return item.txt
  return 'Unknown'
}

// Strip CIDR notation from IP addresses (defensive - backend should handle this)
const stripCIDR = (ip) => {
  if (!ip) return ip
  // Remove /32, /24, etc. from IP addresses
  const slashIndex = ip.indexOf('/')
  return slashIndex !== -1 ? ip.substring(0, slashIndex) : ip
}

// Check if item is clickable
const isClickable = (item) => {
  return item.item_type === 1 // Only IPs are clickable
}

// Handle item click
const handleItemClick = (item) => {
  if (item.item_type === 1 && item.ip) {
    // Strip CIDR notation and navigate to IP search page
    const cleanIP = stripCIDR(item.ip)
    router.push({
      name: 'SearchResults',
      query: {
        input: cleanIP,
        type: 'ip'
      }
    })
  }
  // Domains and URLs are not clickable
}

// Format timestamp
const formatTimestamp = (timestamp) => {
  return moment(timestamp).fromNow()
}

// Setup intersection observer
const setupIntersectionObserver = () => {
  if (!loadMoreTrigger.value) return

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry.isIntersecting && hasMore.value && !loadingMore.value) {
        fetchDataItems(true)
      }
    },
    {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    }
  )

  observer.observe(loadMoreTrigger.value)
}

// Lifecycle hooks
onMounted(() => {
  fetchDataItems()

  // Setup observer after initial load
  setTimeout(() => {
    setupIntersectionObserver()
  }, 100)
})

onBeforeUnmount(() => {
  if (observer && loadMoreTrigger.value) {
    observer.unobserve(loadMoreTrigger.value)
    observer.disconnect()
  }
})
</script>

<style scoped>
.data-items-list {
  width: 100%;
}

/* Aggregation info banner */
.aggregation-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 0.5rem;
  color: #3b82f6;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.2);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin-top: 16px;
  color: #9ca3af;
  font-size: 14px;
}

/* Items Container */
.items-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Data Item Card */
.data-item-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 0.75rem;
  padding: 1rem;
  transition: all 0.2s ease;
}

.data-item-card.clickable {
  cursor: pointer;
}

.data-item-card.clickable:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(59, 130, 246, 0.3);
  transform: translateY(-1px);
}

/* Item Header */
.item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.type-badge.type-ip {
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.type-badge.type-domain {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.type-badge.type-url {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.category-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: rgba(168, 85, 247, 0.15);
  color: #a855f7;
  border: 1px solid rgba(168, 85, 247, 0.3);
}

/* Item Value */
.item-value {
  font-size: 16px;
  font-weight: 600;
  color: #f3f4f6;
  margin-bottom: 8px;
  word-break: break-all;
  line-height: 1.5;
}

/* Item Description */
.item-description {
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 12px;
  line-height: 1.6;
}

/* Item Footer */
.item-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 12px;
  color: #6b7280;
}

.provider-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.provider-thumb {
  flex-shrink: 0;
}

.provider-name {
  color: #9ca3af;
  font-weight: 500;
}

.timestamp {
  color: #6b7280;
  white-space: nowrap;
}

/* Load More Trigger */
.load-more-trigger {
  height: 20px;
  margin: 20px 0;
}

/* Loading More */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  color: #9ca3af;
  font-size: 14px;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(59, 130, 246, 0.2);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

.empty-icon {
  width: 64px;
  height: 64px;
  color: #4b5563;
  margin-bottom: 16px;
}

.empty-text {
  color: #6b7280;
  font-size: 16px;
  text-align: center;
}

/* Animations */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .data-item-card {
    padding: 12px;
  }

  .item-value {
    font-size: 14px;
  }

  .item-description {
    font-size: 13px;
  }

  .item-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .timestamp {
    align-self: flex-end;
  }
}
</style>
